import React, { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../util/services";
import { mountEmbeddedCheckout } from "../../util/stripeCheckout";
import styles from "./signup.module.scss";

const COPY =
  "Add a card to activate Sonic Prospect. You won't be charged now. Your first 5 qualified leads are free, then $5.99 per lead (first 1,000), $9.99 after, billed monthly. Conversations are included up to 20 answers per visitor a day. Cancel anytime.";

// Fallbacks until /self-serve/status answers; the API checks the version it expects.
const DEFAULT_TERMS_VERSION = "2026-10-18";
const DEFAULT_TERMS_URL = "https://pivony.com/legal/sonic-prospect";

export default function SignupCardStep({ brandName, onReady, termsVersion, termsUrl }) {
  const box = useRef(null);
  const ready = useRef(onReady);
  ready.current = onReady;
  const returning = new URLSearchParams(window.location.search).get("session_id");
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const version = termsVersion || DEFAULT_TERMS_VERSION;
  const url = termsUrl || DEFAULT_TERMS_URL;

  // Returning from Stripe: the Terms were accepted before the card form was shown.
  useEffect(() => {
    apiRequest(
      "subscriptions/sonic-prospect/self-serve/milestone",
      "POST",
      { kind: "email_verified" },
      true
    );
    if (!returning) return;
    apiRequest(
      "subscriptions/sonic-prospect/self-serve/complete",
      "POST",
      { session_id: returning },
      true
    ).then((result) => {
      if (result && result.card_saved && ready.current) ready.current(result);
      else if (result && result.error) setError(result.error);
    });
  }, [returning]);

  // The card form loads only after the Terms box is ticked.
  useEffect(() => {
    if (returning || !agreed) return undefined;
    let checkout;
    let gone = false;
    setError("");
    const returnUrl =
      `${window.location.origin}/signup?product=sonic-prospect&lang=en` +
      "&card=return&session_id={CHECKOUT_SESSION_ID}";
    apiRequest(
      "subscriptions/sonic-prospect/self-serve/setup",
      "POST",
      { return_url: returnUrl, terms_version: version },
      true
    ).then(async (result) => {
      if (gone || !result) return;
      if (result.error || !result.client_secret) {
        setError(result.error || "Card step could not be started. Please try again.");
        return;
      }
      if (!box.current) return;
      checkout = await mountEmbeddedCheckout({
        publishableKey: result.publishable_key,
        clientSecret: result.client_secret,
        container: box.current,
      });
    });

    const abandon = () => {
      apiRequest("subscriptions/sonic-prospect/self-serve/abandon", "POST", {}, true);
    };
    window.addEventListener("pagehide", abandon);
    return () => {
      gone = true;
      window.removeEventListener("pagehide", abandon);
      if (checkout && checkout.destroy) checkout.destroy();
    };
  }, [agreed, returning, version]);

  return (
    <div className={`${styles.container} ${styles.containerActivation}`}>
      <div className={styles.signUpContainer}>
        <div className={styles.activationPanel}>
          {brandName ? (
            <p className={styles.demoBanner}>
              Continuing with your {brandName} assistant
            </p>
          ) : null}
          <h1>Add a card</h1>
          <p>{COPY}</p>
          {returning ? null : (
            <label className={styles.termsRow}>
              <input
                type="checkbox"
                checked={agreed}
                disabled={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
              />
              <span>
                I agree to the{" "}
                <a href={url} target="_blank" rel="noopener noreferrer">
                  Sonic Prospect Terms of Service
                </a>{" "}
                and{" "}
                <a href={`${url}#fees`} target="_blank" rel="noopener noreferrer">
                  Fees Policy
                </a>
                , and I authorize Pivony to charge this card monthly for qualified leads, and for extended answers only if I turn them on, as described there.
              </span>
            </label>
          )}
          {error ? <p className={styles.termsError}>{error}</p> : null}
          <div ref={box} className={styles.cardMount} />
          <p className={styles.activationNote}>
            <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">
              Stripe
            </a>
            {" · "}
            <a href="https://pivony.com/plans#prospect" target="_blank" rel="noopener noreferrer">
              Full pricing
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
